import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth, useDestination, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import FooterMapSection from "../FooterMapSection";
import MarketplaceAccessPanel from "../MarketplaceAccessPanel";
import HelpRequestCard from "../HelpRequestCard";
import { getEcosystemBrand } from "../../config/ecosystemMetadata";
import {
  Globe,
  ShoppingCart,
  User,
  Menu,
  Package,
  LogOut,
  LayoutDashboard,
  Search,
  Sparkles,
  ExternalLink,
  Phone,
  Mail,
  MapPin,
  Clock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { listShopDestinations } from "../../config/caribbeanDestinations";

const destinations = listShopDestinations();

function navLinkClass(isActive, accent = false) {
  return [
    "nav-link",
    accent ? "nav-link-accent" : "",
    isActive ? "nav-link-active" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

export default function Layout({ children }) {
  const cheepBrand = getEcosystemBrand("cheepsweepz");
  const { user, logout } = useAuth();
  const { destination, setDestination, currency, setCurrency } = useDestination();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [urlSearchOpen, setUrlSearchOpen] = useState(false);
  const [marketplacesOpen, setMarketplacesOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Check if user is a customer (authenticated and not admin)
  const isCustomer = user && user.role !== "admin";

  const handleDestinationChange = (value) => {
    const dest = destinations.find((d) => d.code === value);
    setDestination(value);
    setCurrency(dest?.currency || "USD");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const currentDest = destinations.find((d) => d.code === destination);

  const isNavActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const desktopNavItems = [
    { to: "/products", label: "Products", testId: "nav-products" },
    { to: "/global-shipping", label: "Global Shipping", testId: "nav-global-shipping", accent: true },
    { to: "/submit", label: "Submit Link", testId: "nav-submit", accent: true },
    ...(user?.role === "admin"
      ? [{ to: "/admin/submissions", label: "Submissions", testId: "nav-admin-submissions" }]
      : []),
    ...(isCustomer ? [{ to: "/my-catalog", label: "My Catalog", testId: "nav-catalog" }] : []),
    { to: "/orders", label: "Orders", testId: "nav-orders" },
    { to: "/community", label: "Chat", testId: "nav-community" },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header — primary bar + desktop sub-nav for breathing room */}
      <header className="fixed top-[40px] left-0 right-0 z-[99999] glass border-b border-slate-200 shadow-sm">
        {/* Primary bar: brand, search, utilities */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 sm:gap-4 lg:gap-6 h-16 min-w-0">
            {/* Logo — full brand name on all widths (no truncate) */}
            <Link
              to="/"
              className="flex items-center gap-1.5 sm:gap-2 shrink-0 min-w-0"
              data-testid="logo-link"
            >
              <Sparkles className="h-6 w-6 sm:h-8 sm:w-8 text-orange-600 shrink-0" />
              <div className="flex flex-col leading-none">
                <span className="font-bold text-[15px] sm:text-lg lg:text-xl tracking-tight text-slate-900 whitespace-nowrap">
                  CheepSweepz
                </span>
                <span className="text-[9px] sm:text-[10px] text-orange-600 font-semibold mt-0.5 tracking-[0.12em] sm:tracking-[0.18em]">
                  CONCIERGE
                </span>
              </div>
            </Link>

            {/* Product Search — desktop: input + button side-by-side so text is never clipped */}
            <div className="hidden lg:flex flex-1 items-center gap-2 min-w-[220px] max-w-3xl mx-3 xl:mx-6">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const query = searchQuery.trim();
                  if (!query) return;
                  if (query.match(/^https?:\/\//i)) {
                    toast.info("To request a new product, use 'Submit Product Link'");
                    if (isCustomer) navigate("/submit");
                    return;
                  }
                  navigate(`/products?search=${encodeURIComponent(query)}`);
                  setSearchQuery("");
                }}
                className="flex flex-1 items-center gap-2 min-w-0"
              >
                <div className="relative flex-1 min-w-[140px]">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  <Input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="pl-10 pr-3 h-11 w-full bg-white border-slate-200 shadow-sm text-sm text-slate-900 placeholder:text-slate-400"
                    data-testid="product-search-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="h-11 shrink-0 bg-orange-500 hover:bg-orange-600 text-white px-4"
                  data-testid="product-search-button"
                >
                  <Search className="h-4 w-4 lg:mr-1.5" />
                  <span className="hidden xl:inline">Search</span>
                </Button>
              </form>
              <Dialog open={marketplacesOpen} onOpenChange={setMarketplacesOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-11 shrink-0 px-3.5 text-slate-600 border-slate-200 bg-white/80 hover:border-orange-400 hover:text-orange-600"
                    data-testid="browse-marketplaces-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-1.5" />
                    Browse
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Browse Marketplaces</DialogTitle>
                  </DialogHeader>
                  <MarketplaceAccessPanel
                    variant="default"
                    pasteTarget="submit-page"
                    showSubmitCta
                    onSubmitClick={() => {
                      setMarketplacesOpen(false);
                      navigate("/submit");
                    }}
                  />
                  <HelpRequestCard variant="default" className="mt-4" />
                </DialogContent>
              </Dialog>
            </div>

            {/* Utilities — keep menu button always reachable on small screens */}
            <div className="flex items-center gap-1 sm:gap-1.5 ml-auto shrink-0 min-w-0">
              {/* Mobile Search Button */}
              <Dialog open={urlSearchOpen} onOpenChange={setUrlSearchOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="lg:hidden"
                    data-testid="mobile-search"
                  >
                    <Search className="h-5 w-5" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Search Products</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={(e) => {
                    e.preventDefault();
                    const query = searchQuery.trim();
                    if (!query) return;
                    // Block URL patterns
                    if (query.match(/^https?:\/\//i)) {
                      toast.info("To request a new product, use 'Submit Product Link'");
                      setUrlSearchOpen(false);
                      if (isCustomer) navigate("/submit");
                      return;
                    }
                    navigate(`/products?search=${encodeURIComponent(query)}`);
                    setSearchQuery("");
                    setUrlSearchOpen(false);
                  }} className="space-y-4">
                    <div>
                      <Input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search available products..."
                        className="w-full"
                      />
                      <p className="text-xs text-slate-500 mt-2">
                        Search our catalog of available products
                      </p>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-orange-500 hover:bg-orange-600"
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Search Products
                    </Button>
                  </form>
                  {/* Marketplace Links */}
                  <MarketplaceAccessPanel
                    variant="compact"
                    className="mt-4"
                    pasteTarget="submit-page"
                    showSubmitCta
                    onSubmitClick={() => {
                      setUrlSearchOpen(false);
                      navigate("/submit");
                    }}
                  />
                  {/* Help Request */}
                  <HelpRequestCard variant="compact" className="mt-3" />
                </DialogContent>
              </Dialog>

            {/* Destination Selector — icon-only on the smallest phones */}
              <Select value={destination} onValueChange={handleDestinationChange}>
                <SelectTrigger
                  className="w-14 xs:w-[72px] sm:w-[108px] h-9 sm:h-10 bg-white/80 border-slate-200 shadow-sm px-1.5 sm:px-2"
                  data-testid="destination-selector"
                >
                  <Globe className="h-4 w-4 mr-1 text-slate-500 shrink-0 hidden sm:block" />
                  <SelectValue>
                    <span className="flex items-center gap-0.5 sm:gap-1 text-xs sm:text-sm">
                      <span>{currentDest?.flag}</span>
                      <span className="hidden xs:inline">{currentDest?.code}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {destinations.map((dest) => (
                    <SelectItem key={dest.code} value={dest.code} data-testid={`dest-${dest.code}`}>
                      <span className="flex items-center gap-2">
                        <span>{dest.flag}</span>
                        <span>{dest.name}</span>
                        <span className="text-slate-400 text-xs ml-auto">
                          {dest.currency}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Cart */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/cart")}
                className="relative"
                data-testid="cart-button"
              >
                <ShoppingCart className="h-5 w-5" />
              </Button>

              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="user-menu-button">
                      <User className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">
                      <Package className="h-4 w-4 mr-2" />
                      My Orders
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                          <LayoutDashboard className="h-4 w-4 mr-2" />
                          Admin Dashboard
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="default"
                  size="icon"
                  onClick={() => navigate("/login")}
                  className="h-9 w-9 sm:h-10 sm:w-auto sm:px-4 bg-slate-900 hover:bg-slate-800 shadow-sm shrink-0"
                  data-testid="login-button"
                  aria-label="Sign In"
                >
                  <User className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Sign In</span>
                </Button>
              )}

              {/* Mobile Menu — always last so it stays visible */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden shrink-0">
                  <Button variant="outline" size="icon" className="h-9 w-9 sm:h-10 sm:w-10 border-slate-200" data-testid="mobile-menu-button" aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[min(100vw-2rem,320px)] overflow-y-auto">
                  <nav className="flex flex-col gap-1 mt-6 pb-8">
                    {desktopNavItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={`text-base font-medium py-3 px-3 rounded-lg transition-colors ${
                          isNavActive(item.to)
                            ? item.accent
                              ? "bg-orange-50 text-orange-700"
                              : "bg-slate-100 text-slate-900"
                            : item.accent
                              ? "text-orange-600 hover:bg-orange-50"
                              : "text-slate-700 hover:bg-slate-50"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                        data-testid={item.testId}
                      >
                        {item.label}
                      </Link>
                    ))}
                    <div className="my-2 border-t border-slate-100" />
                    <Link
                      to="/cart"
                      className="text-base font-medium py-3 px-3 rounded-lg text-slate-700 hover:bg-slate-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Cart
                    </Link>
                    {user?.role === "admin" && (
                      <Link
                        to="/admin"
                        className="text-base font-medium py-3 px-3 rounded-lg text-slate-700 hover:bg-slate-50"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Admin Dashboard
                      </Link>
                    )}
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>

        {/* Mobile sub-navigation — always visible below primary bar */}
        <div className="lg:hidden border-t border-slate-200/80 bg-white/95 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-2 sm:px-4">
            <nav
              className="flex items-center gap-1 h-11 overflow-x-auto scrollbar-none py-1"
              aria-label="Mobile site navigation"
            >
              {desktopNavItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                    isNavActive(item.to)
                      ? item.accent
                        ? "bg-orange-100 text-orange-700"
                        : "bg-slate-100 text-slate-900"
                      : item.accent
                        ? "text-orange-600 hover:bg-orange-50"
                        : "text-slate-600 hover:bg-slate-50"
                  }`}
                  data-testid={`mobile-${item.testId}`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>

        {/* Desktop sub-navigation */}
        <div className="hidden lg:block site-header-subnav border-t border-slate-200/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex items-center gap-0.5 h-11 overflow-x-auto scrollbar-none">
              {desktopNavItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={navLinkClass(isNavActive(item.to), item.accent)}
                  data-testid={item.testId}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content — ticker (40px) + primary bar (64px) + mobile sub-nav (44px) */}
      <main className="flex-1 pt-[calc(148px+env(safe-area-inset-top,0px))] lg:pt-[148px] min-h-0 w-full overflow-x-hidden">
        {children}
      </main>

      {/* Footer Map Section */}
      <FooterMapSection />

      {/* Footer */}
      <footer className="bg-slate-900 text-white noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="h-8 w-8 text-orange-500" />
                <div>
                  <span className="font-bold text-xl">CheepSweepz</span>
                  <span className="text-orange-400 text-xs ml-1">CONCIERGE</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm max-w-md mb-4">
                Your personal shopping concierge for global marketplaces. We handle customs,
                freight, taxes, and delivery — you just pay one landed price.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-2 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-orange-500" />
                  <a href="tel:+12468411248" className="hover:text-white transition">+1 246-841-1248</a>
                  <span className="text-slate-600">|</span>
                  <a href="tel:+12464273323" className="hover:text-white transition">+1 246-427-3323</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-orange-500" />
                  <a href={`mailto:${cheepBrand.supportEmail}`} className="hover:text-white transition">
                    {cheepBrand.supportEmail}
                  </a>
                </div>
                {cheepBrand.ordersEmail ? (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-orange-500" />
                    <a href={`mailto:${cheepBrand.ordersEmail}`} className="hover:text-white transition">
                      {cheepBrand.ordersEmail}
                    </a>
                    <span className="text-slate-600 text-xs">(orders)</span>
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-orange-500" />
                  <span>Bridgetown, St. Michael, Barbados</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-500" />
                  <span>Open 24 hours</span>
                </div>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">
                Shop From
              </h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link to="/products?marketplace=amazon" className="hover:text-white transition">
                    Amazon
                  </Link>
                </li>
                <li>
                  <Link to="/products?marketplace=alibaba" className="hover:text-white transition">
                    Alibaba
                  </Link>
                </li>
                <li>
                  <Link to="/products?marketplace=aliexpress" className="hover:text-white transition">
                    AliExpress
                  </Link>
                </li>
                <li>
                  <Link to="/products?marketplace=ebay" className="hover:text-white transition">
                    eBay
                  </Link>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">
                Support
              </h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>
                  <Link to="/global-shipping" className="hover:text-white transition font-medium text-orange-300">
                    Cross-Country Shipping
                  </Link>
                </li>
                <li>
                  <Link to="/orders" className="hover:text-white transition">
                    Track Order
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="hover:text-white transition">
                    Shipping Info
                  </Link>
                </li>
                <li>
                  <Link to="/terms#customs" className="hover:text-white transition">
                    Customs Guide
                  </Link>
                </li>
                <li>
                  <a 
                    href="https://www.google.com/maps/place/CheepSweepz" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:text-white transition flex items-center gap-1"
                  >
                    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Find us on Google
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* slate-500 on the navy footer measures 3.75:1 and slate-600 2.36:1;
              slate-400 clears AA while staying visually secondary. */}
          <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} CheepSweepz Concierge. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-slate-400 text-sm justify-center md:justify-end">
              <a href="https://digitalhustlerscaribbean.com/terms" className="hover:text-white transition">Terms</a>
              <a href="https://digitalhustlerscaribbean.com/privacy" className="hover:text-white transition">Privacy</a>
              <a href="https://digitalhustlerscaribbean.com/cookies" className="hover:text-white transition">Cookies</a>
            </div>
          </div>
          {/* Version stamp for deployment verification */}
          <div className="text-center mt-4">
            <span className="text-slate-400 text-xs" data-testid="version-stamp">
              v2.2.1 • Build 2026-06-20
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
